import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/Button';
import { Alert, AlertTitle, AlertDescription } from './ui/Alert';

// Show warning when 5 minutes remain
const SESSION_WARNING_THRESHOLD = 300; 

const SessionWarning: React.FC = () => {
  const [showWarning, setShowWarning] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { signOut } = useAuth();

  // Function to decode JWT token
  const decodeToken = (token: string) => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(function(c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  };

  // Format seconds to readable time
  const formatTime = (seconds: number) => {
    if (seconds <= 0) return "0s";
    
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    
    if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    } else {
      return `${remainingSeconds}s`;
    }
  };

  useEffect(() => {
    const checkTokenExpiration = () => {
      const idToken = localStorage.getItem('idToken');
      if (!idToken) {
        setShowWarning(false);
        return;
      }
      
      try {
        const decodedToken = decodeToken(idToken);
        if (!decodedToken) {
          setShowWarning(false);
          return;
        }
        
        const expirationTime = decodedToken.exp;
        const currentTime = Math.floor(Date.now() / 1000);
        const secondsRemaining = expirationTime - currentTime;
        
        setTimeRemaining(formatTime(secondsRemaining));
        
        // Show warning when session is about to expire
        if (secondsRemaining > 0 && secondsRemaining < SESSION_WARNING_THRESHOLD) {
          setShowWarning(true);
        } else {
          setShowWarning(false);
        }
        
        // If token is already expired, redirect to login
        if (secondsRemaining <= 0) {
          signOut();
        }
      } catch (error) {
        console.error('Error checking token expiration:', error);
        setShowWarning(false);
      }
    };
    
    // Check token expiration immediately
    checkTokenExpiration();
    
    // Set interval to check every 3 seconds
    const intervalId = setInterval(checkTokenExpiration, 3000);
    
    return () => {
      clearInterval(intervalId);
    };
  }, [signOut]);

  // Handle refresh action - this would need to be implemented in AuthContext
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      // For now, we'll just sign out and redirect to login
      // TODO: Can use refresh token to get new token
      await signOut();
      window.location.href = '/login';
    } catch (error) {
      console.error('Error refreshing session:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  if (!showWarning) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 w-80">
      <Alert variant="warning">
        <AlertTitle>Session Expiring Soon</AlertTitle>
        <AlertDescription>
          {/* <p className="mb-2">Your session will expire in {timeRemaining}.</p> */}
          <p className="mb-2">Your session will expire in less than 5 minutes.</p>
          <p className="mb-2">If you don't sign in again, any unsaved changes will be lost.</p>
          <p className="mb-2">Please sign in again to continue.</p>
          <div className="flex justify-end space-x-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleRefresh} 
              disabled={isRefreshing}
            >
              {isRefreshing ? 'Redirecting...' : 'Sign in again'}
            </Button>
          </div>
        </AlertDescription>
      </Alert>
    </div>
  );
};

export default SessionWarning;